import { UseQueryOptions } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';

import APIError from 'types/api_error';
import { DashboardTemplateSummary } from 'types/dashboard_template';
import { CursorPaginated, CursorPaginationParams } from 'types/pagination';

import { useAPI } from 'hooks/api/useAPI';
import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';

export type UseDashboardTemplatesParams = {
  only_included_in_plan?: boolean;
  query?: string;
};

type UseDashboardTemplatesOptions = UseQueryOptions<
  DashboardTemplateSummary[],
  APIError
> & {
  params?: UseDashboardTemplatesParams;
};

export function useDashboardTemplates({
  queryKey = ['dashboard-templates'],
  params,
  ...options
}: UseDashboardTemplatesOptions = {}) {
  const { applicationID, client } = useAPI();

  const queryParams = {
    only_included_in_plan: true,
    ...params,
  };

  return useAuthenticatedQuery(
    [...queryKey, applicationID, queryParams],
    () => listDashboardTemplates(client, applicationID, queryParams),
    options
  );
}

export async function listDashboardTemplates(
  client: AxiosInstance,
  applicationID: string | undefined,
  params: UseDashboardTemplatesParams
) {
  if (!applicationID) {
    throw {
      code: 'missing-route-parameters',
      status: 400,
      message: 'Missing application route parameter',
    } satisfies APIError;
  }

  const templates: DashboardTemplateSummary[] = [];
  let nextCursor: string | null = null;

  do {
    const queryParams: UseDashboardTemplatesParams & CursorPaginationParams = {
      ...params,
      pagination: 'cursor',
      limit: 150,
    };

    if (nextCursor) {
      queryParams.next_cursor = nextCursor;
    }

    const { data } = await client.get<
      CursorPaginated<DashboardTemplateSummary>
    >(`/internal/apps/${applicationID}/dashboard_templates`, {
      params: queryParams,
    });

    templates.push(...data.data);
    nextCursor = data.next_cursor;
  } while (nextCursor !== null);

  return templates;
}
